import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMemoObjectStore } from '../../stores/memoObjectStore';
import { memoObjectKeys } from '../api/memoObjectAPI';
import { useAsyncOperations } from './asyncCommon';
import {
  useMemoObjectQuery,
  useCreateMemoObjectMutation,
  useUpdateMemoObjectMutation
} from '../query/memoObjectQuery';

/**
 * Memo Object集成Hook - 统一处理memo object相关的数据操作和状态管理
 */

/**
 * Store状态管理hooks - 直接访问store状态
 */

// Store数据hooks
export const useMemoObjectStoreData = () => {
  const memoObjects = useMemoObjectStore((state: any) => state.memoObjects);
  const currentMemoObject = useMemoObjectStore((state: any) => state.currentMemoObject);
  const status = useMemoObjectStore((state: any) => state.status);
  const isSyncing = useMemoObjectStore((state: any) => state.isSyncing);
  const error = useMemoObjectStore((state: any) => state.error);
  const searchObjects = useMemoObjectStore((state: any) => state.searchObjects);
  const selectedObjects = useMemoObjectStore((state: any) => state.selectedObjects);
  const isLoading = useMemoObjectStore((state: any) => state.isLoading);
  
  return {
    memoObjects,
    currentMemoObject,
    status,
    isSyncing,
    error,
    searchObjects,
    selectedObjects,
    isLoading
  };
};

// Store操作hooks
export const useMemoObjectStoreActions = () => {
  const setMemoObjects = useMemoObjectStore((state: any) => state.setMemoObjects);
  const addMemoObject = useMemoObjectStore((state: any) => state.addMemoObject);
  const setCurrentMemoObjectByMemoId = useMemoObjectStore((state: any) => state.setCurrentMemoObjectByMemoId);
  const forceRefreshMemoObjectByMemoId = useMemoObjectStore((state: any) => state.forceRefreshMemoObjectByMemoId);
  const setStatus = useMemoObjectStore((state: any) => state.setStatus);
  const setIsSyncing = useMemoObjectStore((state: any) => state.setIsSyncing);
  const setError = useMemoObjectStore((state: any) => state.setError);
  const clearError = useMemoObjectStore((state: any) => state.clearError);
  const clear = useMemoObjectStore((state: any) => state.clear);
  const setSearchObjects = useMemoObjectStore((state: any) => state.setSearchObjects);
  const setSelectedObjects = useMemoObjectStore((state: any) => state.setSelectedObjects);
  const addSelectedObject = useMemoObjectStore((state: any) => state.addSelectedObject);
  const removeSelectedObject = useMemoObjectStore((state: any) => state.removeSelectedObject);
  const setLoading = useMemoObjectStore((state: any) => state.setLoading);
  
  return {
    setMemoObjects,
    addMemoObject,
    setCurrentMemoObjectByMemoId,
    forceRefreshMemoObjectByMemoId,
    setStatus,
    setIsSyncing,
    setError,
    clearError,
    clear,
    setSearchObjects,
    setSelectedObjects,
    addSelectedObject,
    removeSelectedObject,
    setLoading
  };
};

/**
 * 组合管理hooks - 提供高级数据管理功能
 */

// Memo object管理hook
export const useMemoObjectManager = (memoId: string) => {
  const storeData = useMemoObjectStoreData();
  const storeActions = useMemoObjectStoreActions();
  const { safeAsyncOperation } = useAsyncOperations();
  const queryClient = useQueryClient();
  
  // 使用ref来跟踪上一次的memoId，避免不必要的重新渲染
  const prevMemoIdRef = React.useRef<string | null>(null);
  
  // 使用query hooks
  const { data: memoObjectData, isLoading: queryLoading, refetch } = useMemoObjectQuery(memoId);
  const { mutateAsync: createMemoObject } = useCreateMemoObjectMutation();
  const { mutateAsync: updateMemoObject } = useUpdateMemoObjectMutation();

  // 数据同步逻辑：将React Query数据同步到Zustand store
  React.useEffect(() => {
    if (memoObjectData && !queryLoading && memoId) {
      // 只有当store中无数据时才用React Query数据初始化store，避免覆盖乐观更新
      const currentStoreData = useMemoObjectStore.getState().currentMemoObject;
      if (!currentStoreData || currentStoreData.memoId !== memoId) {
        storeActions.setCurrentMemoObjectByMemoId(memoObjectData);
        storeActions.setStatus('idle');
      }
    }
  }, [memoObjectData, queryLoading, memoId]);

  // 添加状态跟踪是否应该强制刷新
  const [forceRefreshFlag, setForceRefreshFlag] = React.useState(false);
  
  // 获取memo object数据
  const getMemoObject = React.useCallback(async (targetMemoId: string) => {
    const result = await safeAsyncOperation(async () => {
      storeActions.setStatus('loading');
      storeActions.clearError();
      
      // 使用React Query查询获取数据
      const data = await queryClient.fetchQuery({
        queryKey: memoObjectKeys.memoObjects(targetMemoId),
        queryFn: async () => {
          // 这里会触发useMemoObjectQuery中的逻辑
          return null; // 实际数据由useMemoObjectQuery处理
        }
      });
      
      if (data) {
        storeActions.setStatus('idle');
        return data;
      } else {
        storeActions.setStatus('idle');
        storeActions.setError(`未找到memoId为"${targetMemoId}"的memo object`);
        return null;
      }
    }, `获取memoId为"${targetMemoId}"的memo object失败`);
    
    return result.data || null;
  }, [safeAsyncOperation, storeActions, queryClient]);

  // 创建memo object
  const createMemoObjectWithSync = React.useCallback(async (targetMemoId: string, objects: any[]) => {
    const result = await safeAsyncOperation(async () => {
      storeActions.setStatus('loading');
      storeActions.clearError();
      
      const result = await createMemoObject({ memoId: targetMemoId, objects });
      
      storeActions.setStatus('idle');
      return result;
    }, '创建memo object失败');
    
    return result.data || null;
  }, [safeAsyncOperation, createMemoObject]);

  // 更新memo object
  const updateMemoObjectWithSync = React.useCallback(async (targetMemoId: string, objects: any[]) => {
    const result = await safeAsyncOperation(async () => {
      storeActions.setStatus('loading');
      storeActions.clearError();
      
      const result = await updateMemoObject({ memoId: targetMemoId, objects });
      
      storeActions.setStatus('idle');
      return result;
    }, '更新memo object失败');
    
    return result.data || null;
  }, [safeAsyncOperation, updateMemoObject]);
  
  useEffect(() => {
    if (!memoId) return;
    
    // 检查memoId是否发生变化
    const memoIdChanged = memoId !== prevMemoIdRef.current;
    prevMemoIdRef.current = memoId;
    
    // 如果设置了强制刷新标志，优先从服务器获取最新数据
    if (forceRefreshFlag) {
      refetch();
      setForceRefreshFlag(false);
    }
    
    // 如果memoId发生变化，重新获取数据
    if (memoIdChanged) {
      getMemoObject(memoId);
    }
  }, [memoId, forceRefreshFlag, getMemoObject, refetch]);

  // 强制刷新数据
  const forceRefresh = React.useCallback(async () => {
    const result = await safeAsyncOperation(async () => {
      storeActions.setStatus('loading');
      storeActions.clearError();
      
      // 使用React Query强制刷新数据
      const data = await refetch();
      
      if (data.data) {
        // 更新store状态
        storeActions.forceRefreshMemoObjectByMemoId(data.data);
        storeActions.setStatus('idle');
        return data.data;
      } else {
        // 清除对应的缓存
        storeActions.forceRefreshMemoObjectByMemoId(null);
        storeActions.setStatus('idle');
        return null;
      }
    }, '强制刷新memo object数据失败');
    
    return result.data || null;
  }, [safeAsyncOperation, storeActions, refetch]);

  // 智能状态判断
  const isReady = React.useMemo(() => {
    return storeData.status === 'idle' && !queryLoading && memoObjectData !== null;
  }, [storeData.status, queryLoading, memoObjectData]);

  const hasError = React.useMemo(() => {
    return storeData.status === 'failed' || !!storeData.error;
  }, [storeData.status, storeData.error]);

  const isSyncing = React.useMemo(() => {
    return storeData.status === 'loading' || queryLoading || storeData.isSyncing;
  }, [storeData.status, queryLoading, storeData.isSyncing]);

  return {
    // Store数据
    ...storeData,
    
    // Store操作
    storeActions,
    
    // 异步操作方法
    getMemoObject,
    createMemoObjectWithSync,
    updateMemoObjectWithSync,
    forceRefresh,
    
    // 状态判断
    isReady,
    hasError,
    isSyncing,
    
    // React Query数据
    memoObjectData,
    queryLoading
  };
};